import React, { useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useTheme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import TextField from "../../../Utils/FormInputs/TextField";
import Label from "../../../Utils/FormInputs/Label";

const useStyles = makeStyles(() => ({
    pregnancyCard: {
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        marginBottom: "10px",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        backgroundColor: "#f7f8fc",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
            backgroundColor: "#eef0f7",
        },
    },
    cardHeaderLeft: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flex: 1,
    },
    pregnancyNumber: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        backgroundColor: "#1976d2",
        color: "#fff",
        fontSize: "12px",
        fontWeight: 700,
        flexShrink: 0,
    },
    summaryText: {
        fontSize: "13px",
        color: "#555",
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        alignItems: "center",
    },
    summaryChip: {
        height: "22px",
        fontSize: "11px",
    },
    cardBody: {
        padding: "12px 16px",
        borderTop: "1px solid #eee",
    },
    emptyState: {
        textAlign: "center",
        padding: "20px 16px",
        border: "2px dashed #d0d4e0",
        borderRadius: "8px",
        backgroundColor: "#fafbfd",
    },
    chevron: {
        fontSize: "11px",
        color: "#888",
        transition: "transform 0.2s ease",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    addRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "8px",
    },
}));

/**
 * PreviousPregnancyTable — Responsive card-based layout for obstetric history.
 * Each pregnancy renders as a collapsible card with form fields in a
 * responsive Bootstrap grid.
 */
const PreviousPregnancyTable = ({
    pregnancies = [],
    onAdd,
    onRemove,
    onChange,
    deliveryModes = [],
    deliveryPlaces = [],
    outcomes = [],
    sexOptions = [],
    submitted = false,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const palette = theme?.palette || {};
    const isDark = palette.type === "dark";

    // Track collapsed cards — by default ALL are expanded.
    // We store indices of *collapsed* cards to avoid initialization issues.
    const [collapsedCards, setCollapsedCards] = useState({});

    const toggleCard = (index) => {
        setCollapsedCards((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const isExpanded = (index) => !collapsedCards[index];

    // Build a short summary for the collapsed card header
    const buildSummary = (pregnancy) => {
        const chips = [];
        if (pregnancy.year) chips.push(pregnancy.year);
        if (pregnancy.mode_of_delivery) {
            const mode = deliveryModes.find((m) => m.value === pregnancy.mode_of_delivery);
            if (mode) chips.push(mode.label);
        }
        if (pregnancy.outcome) {
            const out = outcomes.find((o) => o.value === pregnancy.outcome);
            if (out) chips.push(out.label);
        }
        if (pregnancy.birth_weight) chips.push(`${pregnancy.birth_weight}g`);
        return chips;
    };

    // ─── Empty State ────────────────────────────────────────────────
    if (pregnancies.length === 0) {
        return (
            <div className={classes.emptyState}>
                <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
                    No previous pregnancies recorded.
                </p>
                <button
                    type="button"
                    className="btn btn-sm sms-info-btn"
                    onClick={onAdd}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add Previous Pregnancy
                </button>
            </div>
        );
    }

    // ─── Card-Based Layout ──────────────────────────────────────────
    return (
        <div className="previous-pregnancy-cards">
            <div className={classes.addRow}>
                <button
                    type="button"
                    className="btn btn-sm sms-info-btn"
                    onClick={onAdd}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add Pregnancy
                </button>
            </div>

            {pregnancies.map((pregnancy, index) => {
                const expanded = isExpanded(index);
                const summary = buildSummary(pregnancy);

                return (
                    <div
                        key={index}
                        className={classes.pregnancyCard}
                        style={isDark ? { borderColor: "#444", backgroundColor: "#2a2a2a" } : {}}
                    >
                        {/* Card Header */}
                        <div
                            className={classes.cardHeader}
                            onClick={() => toggleCard(index)}
                            style={isDark ? { backgroundColor: "#333" } : {}}
                        >
                            <div className={classes.cardHeaderLeft}>
                                <span className={classes.pregnancyNumber}>
                                    {pregnancy.pregnancy_number || index + 1}
                                </span>
                                <div className={classes.summaryText}>
                                    <span style={{ fontWeight: 500, fontSize: "13px" }}>
                                        Pregnancy {pregnancy.pregnancy_number || index + 1}
                                    </span>
                                    {!expanded && summary.length > 0 && (
                                        <>
                                            <span style={{ color: "#bbb" }}>—</span>
                                            {summary.map((text, i) => (
                                                <Chip
                                                    key={i}
                                                    label={text}
                                                    size="small"
                                                    variant="outlined"
                                                    className={classes.summaryChip}
                                                />
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className={classes.headerActions}>
                                <Tooltip title="Remove this pregnancy">
                                    <IconButton
                                        type="button"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(index);
                                        }}
                                        style={{ color: "#d32f2f" }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            style={{ fontSize: "12px" }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <FontAwesomeIcon
                                    icon={expanded ? faChevronDown : faChevronRight}
                                    className={classes.chevron}
                                />
                            </div>
                        </div>

                        {/* Card Body — expanded by default */}
                        {expanded && (
                            <div className={classes.cardBody}>
                                <Row>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="Year" />
                                        <TextField
                                            type="number"
                                            value={pregnancy.year || ""}
                                            onChange={(e) =>
                                                onChange(index, "year", e.target.value)
                                            }
                                            placeholder="YYYY"
                                            min="1970"
                                            max={new Date().getFullYear()}
                                        />
                                    </Col>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="GA at Delivery (wks)" />
                                        <TextField
                                            type="number"
                                            value={pregnancy.gestational_age_at_delivery || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "gestational_age_at_delivery",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Weeks"
                                            min="0"
                                            max="45"
                                        />
                                    </Col>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="Mode of Delivery" />
                                        <select
                                            className={`form__control form-control ${isDark ? "form-control-dark" : ""}`}
                                            value={pregnancy.mode_of_delivery || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "mode_of_delivery",
                                                    e.target.value
                                                )
                                            }
                                            style={{ fontSize: "14px", borderRadius: "10px", height: "34px" }}
                                        >
                                            <option value="">Select...</option>
                                            {deliveryModes.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="Place of Delivery" />
                                        <select
                                            className={`form__control form-control ${isDark ? "form-control-dark" : ""}`}
                                            value={pregnancy.place_of_delivery || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "place_of_delivery",
                                                    e.target.value
                                                )
                                            }
                                            style={{ fontSize: "14px", borderRadius: "10px", height: "34px" }}
                                        >
                                            <option value="">Select...</option>
                                            {deliveryPlaces.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="Birth Weight (g)" />
                                        <TextField
                                            type="number"
                                            value={pregnancy.birth_weight || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "birth_weight",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="grams"
                                            min="0"
                                            max="7000"
                                        />
                                    </Col>
                                    <Col xs={6} sm={4} md={3} className="mb-2">
                                        <Label name="Sex" />
                                        <select
                                            className={`form__control form-control ${isDark ? "form-control-dark" : ""}`}
                                            value={pregnancy.sex || ""}
                                            onChange={(e) =>
                                                onChange(index, "sex", e.target.value)
                                            }
                                            style={{ fontSize: "14px", borderRadius: "10px", height: "34px" }}
                                        >
                                            <option value="">—</option>
                                            {sexOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                    <Col xs={12} sm={4} md={3} className="mb-2">
                                        <Label name="Outcome" />
                                        <select
                                            className={`form__control form-control ${isDark ? "form-control-dark" : ""}`}
                                            value={pregnancy.outcome || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "outcome",
                                                    e.target.value
                                                )
                                            }
                                            style={{ fontSize: "14px", borderRadius: "10px", height: "34px" }}
                                        >
                                            <option value="">Select...</option>
                                            {outcomes.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                    <Col xs={12} sm={12} md={3} className="mb-2">
                                        <Label name="Complications" />
                                        <TextField
                                            type="text"
                                            value={pregnancy.complications || ""}
                                            onChange={(e) =>
                                                onChange(
                                                    index,
                                                    "complications",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="e.g. PPH, pre-eclampsia"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                );
            })}

            <div
                className="text-muted mt-1"
                style={{ fontSize: "11px", fontStyle: "italic" }}
            >
                Record all pregnancies excluding the current one. Include
                miscarriages and abortions.
            </div>
        </div>
    );
};

export default PreviousPregnancyTable;
